import { Project } from '../types/project';
import { ProjectFormData } from '../types/project-form';

/**
 * API Configuration
 * This centralizes all API configuration in one place
 */
// Determine the right API URL based on environment
const getApiBaseUrl = () => {
  // First try the environment variable
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // If not set, try to determine based on current host
  const currentHost = window.location.hostname;

  // For local development
  if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    return `http://localhost:${process.env.LOCAL_API_PORT || 4000}/api`;
  }

  // For deployed environments, use relative path
  return '/api';
};

export const ApiConfig = {
  // Base URL determined dynamically
  baseUrl: getApiBaseUrl(),

  // API Version - could be moved to env variable later
  version: 'v1',

  // Default timeout in ms
  timeout: 30000,

  // Default headers
  defaultHeaders: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },

  // Retry configuration
  retry: {
    attempts: 2,
    initialDelay: 1000
  }
};

// Test Cycle type definition
export interface TestCycle {
  _id?: string;
  id?: string;
  projectId: string;
  startDate: string;
  endDate: string;
  metrics: Record<string, number>;
  comments: string;
  createdAt?: string;
  updatedAt?: string;
}

// For backward compatibility
export const API_BASE_URL = ApiConfig.baseUrl;
const API_URL = API_BASE_URL;

/**
 * API Error interface - provides structured error information
 */
export interface ApiError extends Error {
  status?: number;
  details?: any;
  isApiError: boolean;
}

/**
 * Enhanced API error handling helper
 * Parses API errors and provides structured error information
 */
const handleResponse = async (response: Response) => {

  if (!response.ok) {
    const errorText = await response.text();

    // Create a structured error object
    const error = new Error() as ApiError;
    error.status = response.status;
    error.isApiError = true;

    try {
      // Try to parse as JSON
      const errorJson = JSON.parse(errorText);
      error.message = errorJson.message || `API Error: ${response.status}`;
      error.details = errorJson.details || errorJson;
    } catch (e) {
      // If not JSON, use the text as is
      error.message = errorText || `API Error: ${response.status}`;
    }

    throw error;
  }

  return response.json();
};

/**
 * Enhanced retry mechanism with exponential backoff
 * Allows for retrying failed API calls with configurable retry logic
 */
const withRetry = async <T>(fn: () => Promise<T>, retries = ApiConfig.retry.attempts, delay = ApiConfig.retry.initialDelay): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (retries <= 0) throw error;

    // Wait for the specified delay
    await new Promise(resolve => setTimeout(resolve, delay));

    return withRetry(fn, retries - 1, delay * 1.5); // Exponential backoff
  }
};

// Project API service
export const projectService = {
  // Save a project as draft
  saveProjectAsDraft: async (project: ProjectFormData): Promise<Project> => {

    // Import adapters
    const { adaptProjectForApi, adaptProjectFromApi } = require('./adapters');

    // Create a copy and explicitly set status to draft
    const draftProject = { ...project, status: 'draft' };
    const adaptedProject = adaptProjectForApi(draftProject);

    // Safety net: re-force draft status after adaptation in case adapter changed it
    adaptedProject.status = 'draft';

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/projects`, {
        method: 'POST',
        headers: ApiConfig.defaultHeaders,
        body: JSON.stringify(adaptedProject),
      });

      if (!response.ok) {
        // Save to localStorage as backup
        const existingProjects = JSON.parse(localStorage.getItem('draft_projects') || '[]');
        const projectWithId = {
          ...adaptedProject,
          id: `local_draft_${Date.now()}`,
          _id: `local_draft_${Date.now()}`,
        };
        localStorage.setItem('draft_projects', JSON.stringify([...existingProjects, projectWithId]));

        return handleResponse(response);
      }

      const savedProject = await response.json();
      return adaptProjectFromApi(savedProject);
    }).catch(error => {
      console.error('[API] Failed to save draft project after retries:', error);

      // Save to localStorage on error
      try {
        const existingProjects = JSON.parse(localStorage.getItem('draft_projects') || '[]');
        const projectWithId = {
          ...adaptedProject,
          id: `local_draft_${Date.now()}`,
          _id: `local_draft_${Date.now()}`,
        };
        localStorage.setItem('draft_projects', JSON.stringify([...existingProjects, projectWithId]));
        return projectWithId as Project;
      } catch (localStorageError) {
        console.error('[API] Failed to save draft project to localStorage:', localStorageError);
      }

      throw error;
    });
  },
  // Get all projects
  getAllProjects: async (): Promise<Project[]> => {
    return withRetry(async () => {
      const response = await fetch(`${API_URL}/projects`);
      const projects = await handleResponse(response);

      // Post-process projects to ensure location hierarchy is complete
      return projects.map((project: Project) => {
        const enhancedProject = { ...project };

        // If project has facility but missing subcounty/county, derive them
        if (enhancedProject.facility && (!enhancedProject.subcounty || !enhancedProject.county)) {

          // Import location data
          const { facilities, subCounties } = require('../data/locations');

          // Find facility in our known facilities list
          const facilityData = facilities.find((f: { name: string; subCountyName: string }) => f.name === enhancedProject.facility);

          if (facilityData) {
            // Set subcounty from facility data if missing
            if (!enhancedProject.subcounty) {
              enhancedProject.subcounty = facilityData.subCountyName;
            }

            // Find county from subcounty if missing
            if (!enhancedProject.county && enhancedProject.subcounty) {
              const subcountyData = subCounties.find((sc: { name: string; countyName: string }) => sc.name === enhancedProject.subcounty);
              if (subcountyData) {
                enhancedProject.county = subcountyData.countyName;
              }
            }
          }
        }

        return enhancedProject;
      });
    }).catch(error => {
      console.error('Failed to fetch projects after retries:', error);
      throw error;
    });
  },

  // Get projects by status
  getProjectsByStatus: async (status: string): Promise<Project[]> => {
    return withRetry(async () => {
      const response = await fetch(`${API_URL}/projects/status/${status}`);
      return handleResponse(response);
    }).catch(error => {
      console.error(`Failed to fetch ${status} projects after retries:`, error);
      throw error;
    });
  },

  // Older getProjectById method removed to fix duplicate property issue

  // Create a new project
  createProject: async (project: ProjectFormData): Promise<Project> => {
    try {

      // Explicitly set project status to 'ongoing' before adapter processing
      // This matches how saveProjectAsDraft works (which is working correctly)
      // Also ensure createdBy field is set as it's required by the backend
      const currentUser = localStorage.getItem('userName') || sessionStorage.getItem('userName') || 'Unknown User';
      const projectWithStatus = {
        ...project,
        status: 'ongoing',
        createdBy: project.createdBy || currentUser
      };

      // Use the adapter to transform project for API (handles fishbone, status, defaults)
      // Import from adapters.ts
      const { adaptProjectForApi, adaptProjectFromApi } = require('./adapters');
      const adaptedProject = adaptProjectForApi(projectWithStatus);

      // Always log the status for debugging

      return withRetry(async () => {
        // Debug the request details

        const response = await fetch(`${API_URL}/projects`, {
          method: 'POST',
          headers: ApiConfig.defaultHeaders,
          body: JSON.stringify(adaptedProject),
        });

        // Log the response status for debugging


        // Log response headers for debugging
        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          headers[key] = value;
        });

        if (!response.ok) {
          // Save to localStorage as backup
          const existingProjects = JSON.parse(localStorage.getItem('projects') || '[]');
          const projectWithId = {
            ...adaptedProject,
            id: `local_${Date.now()}`,
            _id: `local_${Date.now()}`,
          };
          localStorage.setItem('projects', JSON.stringify([...existingProjects, projectWithId]));

          // Let handleResponse take care of error formatting
          return handleResponse(response);
        }

        // Transform API response back to frontend model
        const createdProject = await response.json();
        return adaptProjectFromApi(createdProject);
      }).catch(error => {
        console.error('[API] Failed to create project after retries:', error);

        // Always save to localStorage as backup on any error
        try {
          const existingProjects = JSON.parse(localStorage.getItem('projects') || '[]');
          const projectWithId = {
            ...adaptedProject,
            id: `local_${Date.now()}`,
            _id: `local_${Date.now()}`,
          };
          localStorage.setItem('projects', JSON.stringify([...existingProjects, projectWithId]));
          return projectWithId as Project; // Return local backup as fallback
        } catch (localStorageError) {
          console.error('[API] Failed to save project to localStorage:', localStorageError);
        }

        throw error;
      });
    } catch (error) {
      console.error('[API] Failed to create project:', error);
      throw error;
    }
  },

  // Get a project by ID
  getProjectById: async (id: string): Promise<Project | null> => {

    // Import adapters
    const { adaptProjectFromApi } = require('./adapters');

    return withRetry(async () => {
      try {
        // Check if ID is a MongoDB ObjectId (24 hex characters) or a UUID
        const isMongoObjectId = /^[0-9a-f]{24}$/i.test(id);
        const endpoint = isMongoObjectId ? `${API_URL}/projects/${id}` : `${API_URL}/projects/uuid/${id}`;


        const response = await fetch(endpoint, {
          headers: ApiConfig.defaultHeaders
        });

        if (!response.ok) {
          if (response.status === 404) {
            return null;
          }
          // Use our enhanced error handling
          return handleResponse(response);
        }

        const project = await response.json();

        // Transform API response to frontend model
        return adaptProjectFromApi(project);
      } catch (error) {
        console.error(`[API] Error fetching project ${id}:`, error);
        throw error;
      }
    }).catch(error => {
      console.error('[API] Failed to get project after retries:', error);
      throw error;
    });
  },

  // Delete a project
  deleteProject: async (id: string | undefined): Promise<{ message: string }> => {
    // Validate the project ID
    if (!id) {
      throw new Error('Project ID is required to delete a project');
    }


    return withRetry(async () => {
      try {
        const response = await fetch(`${API_URL}/projects/${id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        // Remove from localStorage backup if present
        try {
          const existingProjects = JSON.parse(localStorage.getItem('projects') || '[]');
          const updatedProjects = existingProjects.filter((p: Project) =>
            p.id !== id && p._id !== id
          );
          localStorage.setItem('projects', JSON.stringify(updatedProjects));
        } catch (localStorageError) {
          console.error('[API] Error updating localStorage after project deletion:', localStorageError);
        }

        return result;
      } catch (error) {
        console.error(`[API] Error deleting project ${id}:`, error);
        throw error;
      }
    }).catch(error => {
      console.error('[API] Failed to delete project after retries:', error);
      throw error;
    });
  },

  // Update a project
  updateProject: async (id: string | undefined, project: Partial<Project>): Promise<Project> => {
    // Validate the project ID
    if (!id) {
      throw new Error('Project ID is required to update a project');
    }


    // Import adapters
    const { adaptProjectForApi, adaptProjectFromApi } = require('./adapters');

    // Apply transformations before sending to API
    const adaptedProject = adaptProjectForApi(project as any);

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/projects/${id}`, {
        method: 'PUT',
        headers: ApiConfig.defaultHeaders,
        body: JSON.stringify(adaptedProject),
      });

      if (!response.ok) {
        return handleResponse(response);
      }

      const updatedProject = await response.json();
      return adaptProjectFromApi(updatedProject);
    }).catch(error => {
      console.error(`[API] Failed to update project ${id} after retries:`, error);
      throw error;
    });
  },

  // Add a test cycle to a project (legacy method - use testCycleService.createTestCycle instead)
  addTestCycle: async (projectId: string | undefined, testCycle: any): Promise<Project> => {
    // Validate the project ID
    if (!projectId) {
      throw new Error('Project ID is required to add a test cycle');
    }


    return withRetry(async () => {
      const response = await fetch(`${API_URL}/projects/${projectId}/test-cycles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testCycle),
      });
      return handleResponse(response);
    }).catch(error => {
      console.error(`[API] Failed to add test cycle to project ${projectId} after retries:`, error);
      throw error;
    });
  },
};

// Test Cycle API Service
export const testCycleService = {
  // Get all test cycles for a project
  getTestCyclesByProjectId: async (projectId: string): Promise<TestCycle[]> => {

    // Import adapters
    const { adaptTestCycleFromApi } = require('./adapters');

    return withRetry(async () => {
      try {
        const response = await fetch(`${API_URL}/test-cycles/project/${projectId}`, {
          headers: ApiConfig.defaultHeaders
        });

        if (!response.ok) {
          return handleResponse(response);
        }

        const testCycles = await response.json();

        // Transform each test cycle using the adapter
        return testCycles.map((testCycle: any) => adaptTestCycleFromApi(testCycle));
      } catch (error) {
        console.error(`[API] Error fetching test cycles for project ${projectId}:`, error);
        throw error;
      }
    }).catch(error => {
      console.error(`[API] Failed to get test cycles after retries:`, error);
      throw error;
    });
  },

  // Get a test cycle by ID
  getTestCycleById: async (id: string): Promise<TestCycle | null> => {

    return withRetry(async () => {
      try {
        const response = await fetch(`${API_URL}/test-cycles/${id}`);

        if (!response.ok) {
          if (response.status === 404) {
            return null;
          }
          throw new Error(`API Error: ${response.status} ${response.statusText}`);
        }

        const testCycle = await response.json();
        return testCycle;
      } catch (error) {
        console.error(`[API] Error fetching test cycle ${id}:`, error);
        throw error;
      }
    }).catch(error => {
      console.error('[API] Failed to get test cycle after retries:', error);
      throw error;
    });
  },

  // Create a new test cycle for a project
  createTestCycle: async (projectId: string, testCycle: Omit<TestCycle, '_id' | 'id' | 'projectId' | 'createdAt' | 'updatedAt'>): Promise<TestCycle> => {

    // Import adapters
    const { adaptTestCycleForApi, adaptTestCycleFromApi } = require('./adapters');

    // Transform data before sending to API
    const adaptedTestCycle = adaptTestCycleForApi(testCycle, projectId);

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/test-cycles/project/${projectId}`, {
        method: 'POST',
        headers: ApiConfig.defaultHeaders,
        body: JSON.stringify(adaptedTestCycle),
      });

      if (!response.ok) {
        return handleResponse(response);
      }

      const createdTestCycle = await response.json();
      return adaptTestCycleFromApi(createdTestCycle);
    }).catch(error => {
      console.error(`[API] Failed to create test cycle after retries:`, error);
      throw error;
    });
  },

  // Update a test cycle
  updateTestCycle: async (id: string, testCycle: Partial<TestCycle>): Promise<TestCycle> => {

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/test-cycles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testCycle),
      });
      return handleResponse(response);
    }).catch(error => {
      console.error(`[API] Failed to update test cycle ${id} after retries:`, error);
      throw error;
    });
  },

  // Delete a test cycle
  deleteTestCycle: async (id: string): Promise<{ message: string }> => {

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/test-cycles/${id}`, {
        method: 'DELETE',
      });
      return handleResponse(response);
    }).catch(error => {
      console.error(`[API] Failed to delete test cycle ${id} after retries:`, error);
      throw error;
    });
  },

  // Generate test cycle templates for a project
  generateTestCycleTemplates: async (projectId: string): Promise<{ message: string, templates: TestCycle[] }> => {

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/test-cycles/project/${projectId}/generate-templates`);
      return handleResponse(response);
    }).catch(error => {
      console.error(`[API] Failed to generate test cycle templates for project ${projectId} after retries:`, error);
      throw error;
    });
  },

  // Generate and save test cycles for a project
  generateAndSaveTestCycles: async (projectId: string): Promise<{ message: string, testCycles: TestCycle[] }> => {

    return withRetry(async () => {
      const response = await fetch(`${API_URL}/test-cycles/project/${projectId}/generate`, {
        method: 'POST',
      });
      return handleResponse(response);
    }).catch(error => {
      console.error(`[API] Failed to generate and save test cycles for project ${projectId} after retries:`, error);
      throw error;
    });
  },
};
